/**
 * O invólucro dos diálogos do sistema — spec 01, "Diálogo": 520px
 * centralizado, ícone em tint à esquerda do título.
 *
 * Existe separado porque baixa e lançamento repetem exatamente a mesma
 * moldura, e moldura repetida é como um dos dois perde o `aria-modal` ou o
 * fechar por Esc.
 *
 * `aria-modal="true"` afirma ao leitor de tela que o resto da página deixou de
 * existir, e por isso o foco preso não é opcional: `usarFocoPreso` traz o foco
 * para dentro, fecha o ciclo do Tab e devolve o foco a quem abriu. Sem ele o
 * atributo seria uma promessa que o teclado não cumpre.
 */
import { useId, useRef, type ReactNode } from 'react'

import { Botao, BotaoIcone } from './Botao'
import { Icone, type NomeIcone } from './Icone'
import { usarEsc, usarFocoPreso } from './foco'

export function Dialogo({
  titulo,
  descricao,
  icone = 'payments',
  aoFechar,
  children,
}: {
  titulo: string
  descricao?: ReactNode
  icone?: NomeIcone
  aoFechar: () => void
  children: ReactNode
}) {
  const id = useId()
  const painel = useRef<HTMLDivElement>(null)

  usarFocoPreso(painel)
  usarEsc(aoFechar)

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-[rgba(15,23,42,0.45)] px-4 py-10">
      <div
        ref={painel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        tabIndex={-1}
        className="w-full max-w-[520px] rounded-card bg-superficie p-6 shadow-[0_20px_50px_rgba(15,23,42,0.25)]"
      >
        <div className="mb-5 flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sistema bg-marca-suave text-marca">
            <Icone nome={icone} tamanho={20} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id={id} className="font-titulo text-[17px] font-bold text-titulo">
              {titulo}
            </h2>
            {descricao && (
              <div className="mt-1 text-[13px] leading-[1.5] text-texto-suave">{descricao}</div>
            )}
          </div>
          <BotaoIcone icone="close" rotulo="Fechar" onClick={aoFechar} />
        </div>

        {children}
      </div>
    </div>
  )
}

/**
 * O par de botões do rodapé de um diálogo. O de confirmar é `submit`: quem
 * digita valor e aperta Enter espera que o diálogo conclua, não que nada
 * aconteça.
 */
export function RodapeDoDialogo({
  confirmar,
  confirmando,
  ocupado,
  destrutivo = false,
  aoCancelar,
}: {
  confirmar: string
  confirmando: string
  ocupado: boolean
  /** Ação destrutiva em vermelho — spec 01, "Diálogo". */
  destrutivo?: boolean
  aoCancelar: () => void
}) {
  return (
    <div className="mt-6 flex gap-2.5">
      {/* `flex-1` vai no INVÓLUCRO e não no botão: `Botao` é `shrink-0` de
          propósito, para não ser espremido quando divide a linha com um irmão
          de rótulo longo. Aqui quem manda dividir ao meio é o pai. */}
      <div className="flex-1">
        <Botao variante="secundario" porte="medio" larguraTotal onClick={aoCancelar}>
          Cancelar
        </Botao>
      </div>
      <div className="flex-1">
        <Botao
          type="submit"
          porte="medio"
          larguraTotal
          variante={destrutivo ? 'perigo' : 'primario'}
          disabled={ocupado}
        >
          {ocupado ? confirmando : confirmar}
        </Botao>
      </div>
    </div>
  )
}
